import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import API from '../api/api';
import { useSnackbar } from '../contexts/SnackbarContext';

const prettyJson = (value) => JSON.stringify(value ?? {}, null, 2);

const getParsedRows = (parsedJson) => {
  if (Array.isArray(parsedJson)) return parsedJson;
  if (Array.isArray(parsedJson?.rows)) return parsedJson.rows;
  return [];
};

const getRowColumns = (rows) => {
  const seen = new Set();
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => seen.add(key));
  });
  return Array.from(seen);
};

const statusColor = (status) => {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
      return 'error';
    case 'PARSED':
    case 'REVIEWED':
      return 'warning';
    default:
      return 'default';
  }
};

export default function IncomingDocuments() {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [configRes, documentsRes] = await Promise.all([
          API.get('/incoming-documents/config'),
          API.get('/incoming-documents', { params: { limit: 100 } }),
        ]);

        setConfig(configRes.data);
        setDocuments(documentsRes.data || []);

        const firstId = documentsRes.data?.[0]?.id || null;
        setSelectedId(firstId);
      } catch (err) {
        const message = err.response?.data?.message || 'Dokumente konnten nicht geladen werden';
        showSnackbar(message, 'error');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [showSnackbar]);

  useEffect(() => {
    const loadDetail = async () => {
      if (!selectedId) {
        setSelectedDocument(null);
        return;
      }

      try {
        const res = await API.get(`/incoming-documents/${selectedId}`);
        setSelectedDocument(res.data);
      } catch (err) {
        const message = err.response?.data?.message || 'Dokumentdetail konnte nicht geladen werden';
        showSnackbar(message, 'error');
      }
    };

    loadDetail();
  }, [selectedId, showSnackbar]);

  const handleCopy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value || '');
      showSnackbar(`${label} kopiert`, 'success');
    } catch (err) {
      showSnackbar(`${label} konnte nicht kopiert werden`, 'error');
    }
  };

  const parsedRows = getParsedRows(selectedDocument?.parsed_json);
  const parsedColumns = getRowColumns(parsedRows);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 1.5, sm: 2.5, md: 4 },
        py: { xs: 2, md: 3 },
        background: 'var(--app-bg)',
      }}
    >
      <Stack spacing={3}>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            background: 'var(--surface-1)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <Stack spacing={2}>
            <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 900, color: 'var(--text-primary)' }}>
              Dokumente Inbox
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)' }}>
              Oeffentlicher Eingang fuer n8n, WhatsApp und OCR-Rueckgaben.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, borderRadius: 3, background: 'var(--surface-3)', border: '1px solid var(--border-subtle)' }}>
                  <Stack spacing={1}>
                    <Typography sx={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 800 }}>
                      INGEST URL
                    </Typography>
                    <Typography sx={{ color: 'var(--text-primary)', wordBreak: 'break-all', fontWeight: 700 }}>
                      {config?.ingest_url || '-'}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={() => handleCopy(config?.ingest_url, 'URL')}
                      sx={{ alignSelf: 'flex-start', borderRadius: 999 }}
                    >
                      URL kopieren
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, borderRadius: 3, background: 'var(--surface-3)', border: '1px solid var(--border-subtle)' }}>
                  <Stack spacing={1}>
                    <Typography sx={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 800 }}>
                      BEARER TOKEN
                    </Typography>
                    <Typography sx={{ color: 'var(--text-primary)', wordBreak: 'break-all', fontWeight: 700 }}>
                      {config?.bearer_token || '-'}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={() => handleCopy(config?.bearer_token, 'Token')}
                      sx={{ alignSelf: 'flex-start', borderRadius: 999 }}
                    >
                      Token kopieren
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              n8n soll mit `Authorization: Bearer &lt;token&gt;` an diese URL posten.
            </Alert>
          </Stack>
        </Paper>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                borderRadius: 4,
                background: 'var(--surface-1)',
                border: '1px solid var(--border-subtle)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: 2.2 }}>
                <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                  Empfangene Dokumente
                </Typography>
              </Box>
              <Divider />
              <List sx={{ py: 0 }}>
                {documents.length === 0 && (
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ color: 'var(--text-secondary)' }}>
                      Noch keine Dokumente empfangen.
                    </Typography>
                  </Box>
                )}
                {documents.map((doc) => (
                  <ListItemButton
                    key={doc.id}
                    selected={selectedId === doc.id}
                    onClick={() => setSelectedId(doc.id)}
                    sx={{
                      alignItems: 'flex-start',
                      py: 1.5,
                      px: 2,
                      borderBottom: '1px solid var(--border-subtle)',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <DescriptionIcon sx={{ fontSize: 18, color: 'var(--text-secondary)' }} />
                          <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                            #{doc.id} {doc.document_type || 'document'}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Stack spacing={0.75}>
                          <Typography sx={{ color: 'var(--text-secondary)' }}>
                            {doc.customer_name || doc.customer_phone || 'Unbekannter Absender'}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={doc.status || 'RECEIVED'} color={statusColor(doc.status)} />
                            <Typography sx={{ color: 'var(--text-muted)', fontSize: 12 }}>
                              {doc.received_at || doc.created_at}
                            </Typography>
                          </Stack>
                        </Stack>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                background: 'var(--surface-1)',
                border: '1px solid var(--border-subtle)',
                minHeight: 420,
              }}
            >
              {!selectedDocument ? (
                <Typography sx={{ color: 'var(--text-secondary)' }}>
                  Dokument auswaehlen, um Details zu sehen.
                </Typography>
              ) : (
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                    <Box>
                      <Typography sx={{ color: 'var(--text-primary)', fontSize: 26, fontWeight: 900 }}>
                        Dokument #{selectedDocument.id}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-secondary)' }}>
                        {selectedDocument.document_type || 'document'} • {selectedDocument.customer_name || selectedDocument.customer_phone || 'Unbekannt'}
                      </Typography>
                    </Box>
                    <Chip label={selectedDocument.status || 'RECEIVED'} color={statusColor(selectedDocument.status)} sx={{ alignSelf: 'flex-start' }} />
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, borderRadius: 3, background: 'var(--surface-3)', border: '1px solid var(--border-subtle)' }}>
                        <Stack spacing={1}>
                          <Typography sx={{ color: 'var(--text-muted)', fontWeight: 800, fontSize: 12 }}>
                            BILD URL
                          </Typography>
                          {selectedDocument.image_url ? (
                            <a href={selectedDocument.image_url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                              <Typography sx={{ color: 'var(--text-primary)', wordBreak: 'break-all', fontWeight: 700 }}>
                                {selectedDocument.image_url}
                              </Typography>
                            </a>
                          ) : (
                            <Typography sx={{ color: 'var(--text-secondary)' }}>Kein Bild-Link vorhanden.</Typography>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, borderRadius: 3, background: 'var(--surface-3)', border: '1px solid var(--border-subtle)' }}>
                        <Stack spacing={0.75}>
                          <Typography sx={{ color: 'var(--text-muted)', fontWeight: 800, fontSize: 12 }}>
                            META
                          </Typography>
                          <Typography sx={{ color: 'var(--text-secondary)' }}>Quelle: {selectedDocument.source || '-'}</Typography>
                          <Typography sx={{ color: 'var(--text-secondary)' }}>External ID: {selectedDocument.external_id || '-'}</Typography>
                          <Typography sx={{ color: 'var(--text-secondary)' }}>Confidence: {selectedDocument.confidence ?? '-'}</Typography>
                          <Typography sx={{ color: 'var(--text-secondary)' }}>Empfangen: {selectedDocument.received_at || selectedDocument.created_at || '-'}</Typography>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Paper sx={{ p: 2, borderRadius: 3, background: 'var(--surface-3)', border: '1px solid var(--border-subtle)' }}>
                    <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, mb: 1.2 }}>
                      OCR Text
                    </Typography>
                    <Typography sx={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                      {selectedDocument.raw_text || 'Kein OCR-Text vorhanden.'}
                    </Typography>
                  </Paper>

                  {parsedRows.length > 0 && (
                    <Paper sx={{ p: 2, borderRadius: 3, background: 'var(--surface-3)', border: '1px solid var(--border-subtle)' }}>
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, mb: 1.2 }}>
                        Erkannte Eintraege
                      </Typography>
                      <TableContainer sx={{ borderRadius: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {parsedColumns.map((column) => (
                                <TableCell
                                  key={column}
                                  sx={{
                                    color: 'var(--text-primary)',
                                    fontWeight: 800,
                                    backgroundColor: 'var(--table-header-bg)',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {column}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {parsedRows.map((row, index) => (
                              <TableRow key={index}>
                                {parsedColumns.map((column) => (
                                  <TableCell key={column} sx={{ color: 'var(--text-secondary)', verticalAlign: 'top' }}>
                                    {row?.[column] == null ? '-' : String(row[column])}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={6}>
                      <Paper sx={{ p: 2, borderRadius: 3, background: 'var(--surface-3)', border: '1px solid var(--border-subtle)', height: '100%' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, mb: 1.2 }}>
                          Parsed JSON
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            color: 'var(--text-secondary)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: 13,
                            fontFamily: 'Consolas, Monaco, monospace',
                          }}
                        >
                          {prettyJson(selectedDocument.parsed_json)}
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <Paper sx={{ p: 2, borderRadius: 3, background: 'var(--surface-3)', border: '1px solid var(--border-subtle)', height: '100%' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, mb: 1.2 }}>
                          Meta JSON
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            color: 'var(--text-secondary)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: 13,
                            fontFamily: 'Consolas, Monaco, monospace',
                          }}
                        >
                          {prettyJson(selectedDocument.meta_json)}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
